import { apiClient } from '@/shared/services/api';
import type { ApiResponse } from '@/features/auth/services/authApi';

// ── DTOs (mirror backend FarmSmartERP.Common.DTOs.FinancialYearDtos) ─────────

// Mirrors the backend FarmSmartERP.Common.Enums.FinancialYearStatus integer values.
// The API ships the enum as its underlying int (no JsonStringEnumConverter on .AddControllers),
// matching the convention used by every other status field in this app.
// Using `const`-object pattern (not `enum`) because tsconfig has `erasableSyntaxOnly`.
export const FinancialYearStatus = {
  Open: 1,
  Closing: 2,
  Closed: 3,
  Locked: 4,
  PendingApproval: 5,
} as const;
export type FinancialYearStatus = (typeof FinancialYearStatus)[keyof typeof FinancialYearStatus];

export const FINANCIAL_YEAR_STATUS_LABEL: Record<FinancialYearStatus, string> = {
  [FinancialYearStatus.Open]: 'Open',
  [FinancialYearStatus.Closing]: 'Closing',
  [FinancialYearStatus.Closed]: 'Closed',
  [FinancialYearStatus.Locked]: 'Locked',
  [FinancialYearStatus.PendingApproval]: 'Pending approval',
};

export interface FinancialYearSummaryDto {
  id: string;
  endingYear: number;
  code: string;
  name: string;
  label: string;
  startDate: string;
  endDate: string;
  status: FinancialYearStatus;
  isCurrent: boolean;
}

export interface FinancialYearDto {
  id: string;
  endingYear: number;
  code: string;
  name: string;
  label: string;
  startDate: string;
  endDate: string;
  status: FinancialYearStatus;
  isCurrent: boolean;
  hasTransactions: boolean;
  createdByUserId: string | null;
  approvedByUserId: string | null;
  approvedAt: string | null;
  closingInitiatedByUserId: string | null;
  closingInitiatedAt: string | null;
  closedByUserId: string | null;
  closedAt: string | null;
  closingNotes: string | null;
  lockedByUserId: string | null;
  lockedAt: string | null;
  auditFirmName: string | null;
  auditReportReference: string | null;
  auditSignOffEvidenceUrl: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface PreflightCheckDto {
  id: string;
  label: string;
  passed: boolean;
  isBlocking: boolean;
  failingCount: number;
  linkUrl: string | null;
}

export interface PreflightReportDto {
  financialYearId: string;
  label: string;
  allBlockingPassed: boolean;
  anyWarnings: boolean;
  checks: PreflightCheckDto[];
}

export interface AccountBalanceDto {
  accountId: string;
  accountCode: string;
  accountName: string;
  accountType: string;
  debitBalance: number;
  creditBalance: number;
}

export interface ClosingSnapshotDto {
  financialYearId: string;
  label: string;
  frozenAt: string;
  periodStart: string;
  periodEnd: string;
  trialBalance: AccountBalanceDto[];
  totalIncome: number;
  totalExpense: number;
  netResult: number;
  retainedEarningsAccountId: string;
  closingJournalEntryId: string;
}

export interface ConfirmCloseRequest {
  confirmationLabel: string;
  closingNotes: string;
}

export interface LockRequest {
  confirmationLabel: string;
  auditFirmName: string;
  auditReportReference: string;
  auditSignOffEvidenceUrl?: string | null;
}

export interface CreateFinancialYearRequest {
  code: string;
  name: string;
  startDate: string;
  endDate: string;
}

export interface UpdateFinancialYearRequest {
  code: string;
  name: string;
  startDate: string;
  endDate: string;
}

// ── API calls ────────────────────────────────────────────────────────────────

export const financialYearsApi = {
  list: async (): Promise<FinancialYearSummaryDto[]> => {
    const { data } = await apiClient.get<ApiResponse<FinancialYearSummaryDto[]>>('/financial-years');
    return data.data;
  },

  create: async (payload: CreateFinancialYearRequest): Promise<FinancialYearDto> => {
    const { data } = await apiClient.post<ApiResponse<FinancialYearDto>>('/financial-years', payload);
    return data.data;
  },

  current: async (): Promise<FinancialYearDto> => {
    const { data } = await apiClient.get<ApiResponse<FinancialYearDto>>('/financial-years/current');
    return data.data;
  },

  getById: async (id: string): Promise<FinancialYearDto> => {
    const { data } = await apiClient.get<ApiResponse<FinancialYearDto>>(`/financial-years/${id}`);
    return data.data;
  },

  update: async (id: string, payload: UpdateFinancialYearRequest, gmOverride = false): Promise<FinancialYearDto> => {
    const { data } = await apiClient.put<ApiResponse<FinancialYearDto>>(
      `/financial-years/${id}${gmOverride ? '?gmOverride=true' : ''}`,
      payload,
    );
    return data.data;
  },

  hasTransactions: async (id: string): Promise<boolean> => {
    const { data } = await apiClient.get<ApiResponse<boolean>>(`/financial-years/${id}/has-transactions`);
    return data.data;
  },

  preflight: async (id: string): Promise<PreflightReportDto> => {
    const { data } = await apiClient.get<ApiResponse<PreflightReportDto>>(`/financial-years/${id}/preflight`);
    return data.data;
  },

  snapshot: async (id: string): Promise<ClosingSnapshotDto> => {
    const { data } = await apiClient.get<ApiResponse<ClosingSnapshotDto>>(`/financial-years/${id}/snapshot`);
    return data.data;
  },

  startClose: async (id: string): Promise<FinancialYearDto> => {
    const { data } = await apiClient.post<ApiResponse<FinancialYearDto>>(`/financial-years/${id}/start-close`);
    return data.data;
  },

  confirmClose: async (id: string, payload: ConfirmCloseRequest): Promise<FinancialYearDto> => {
    const { data } = await apiClient.post<ApiResponse<FinancialYearDto>>(
      `/financial-years/${id}/confirm-close`,
      payload,
    );
    return data.data;
  },

  cancelClose: async (id: string): Promise<FinancialYearDto> => {
    const { data } = await apiClient.post<ApiResponse<FinancialYearDto>>(`/financial-years/${id}/cancel-close`);
    return data.data;
  },

  lock: async (id: string, payload: LockRequest): Promise<FinancialYearDto> => {
    const { data } = await apiClient.post<ApiResponse<FinancialYearDto>>(
      `/financial-years/${id}/lock`,
      payload,
    );
    return data.data;
  },

  approveCreation: async (id: string): Promise<FinancialYearDto> => {
    const { data } = await apiClient.post<ApiResponse<FinancialYearDto>>(
      `/financial-years/${id}/approve-creation`,
    );
    return data.data;
  },

  rejectCreation: async (id: string, rejectionReason: string): Promise<FinancialYearDto> => {
    const { data } = await apiClient.post<ApiResponse<FinancialYearDto>>(
      `/financial-years/${id}/reject-creation`,
      { rejectionReason },
    );
    return data.data;
  },
};
